import { useEffect, useMemo, useState } from "react";
import {
  COLORS,
  HoursBar,
  PunchTimes,
  StatusBadge,
  StatusIcon,
  WeekNavigator,
  sumWeeklyHours,
  weeklyHoursColor,
} from "./attendanceUi";
import {
  MONTH_NAMES,
  buildYearOptions,
  defaultWeekIndexForMonth,
  getWeeksInMonth,
} from "./attendanceMonthWeek";
import {
  getAttendanceDisplayMode,
  isDayPillDisabled,
  isFutureSelectedDay,
  todayIso,
} from "./attendanceDayGate";
import { useAdminAttendanceData } from "./useAdminAttendanceData";
import { fetchWfHolidays } from "../../../../Moduels/attendance/wfApi";
import { HrHero, HrPanel } from "../../../hr/HrDesignSystem";
import BrixigoPageLoader from "../../../common/BrixigoPageLoader";
import { LayoutDashboard } from "lucide-react";
import {
  buildHolidayDateMap,
  weekendPillClass,
  WEEKEND_HEADER_STYLES,
} from "../../../../Moduels/attendance/attendanceCalendarUtils";

const STATUS_FILTER_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "present", label: "Present" },
  { value: "absent", label: "Absent" },
  { value: "leave", label: "On Leave" },
  { value: "half-day", label: "Half Day" },
  { value: "week-off", label: "Week Off" },
  { value: "holiday", label: "Holiday" },
];

const now = new Date();

function countByStatus(rows, status) {
  return rows.filter((r) => r.status === status).length;
}

function firstSelectableDayIndex(weekDays) {
  const today = todayIso();
  const todayIdx = weekDays.findIndex((d) => d.date === today);
  if (todayIdx >= 0) return todayIdx;
  for (let i = weekDays.length - 1; i >= 0; i -= 1) {
    if (weekDays[i].date <= today) return i;
  }
  return 0;
}

function heatmapHeaderClass(label) {
  if (label === "Sat") return WEEKEND_HEADER_STYLES.Saturday;
  if (label === "Sun") return WEEKEND_HEADER_STYLES.Sunday;
  return "bg-slate-50 text-slate-600";
}

function heatmapTdClass(label, orgHoliday) {
  if (orgHoliday) return "bg-violet-50/60";
  if (label === "Sat") return "bg-indigo-50/50";
  if (label === "Sun") return "bg-rose-50/50";
  return "";
}

function HeatmapCell({ rec, dateStr, orgHoliday }) {
  const { mode } = getAttendanceDisplayMode(dateStr, rec);

  if (orgHoliday && (mode === "future_empty" || !rec?.status || rec?.isEmpty)) {
    return (
      <div className="flex flex-col items-center gap-1 p-1 rounded-lg bg-violet-100 border border-violet-300">
        <StatusBadge status="holiday" />
        <span className="text-[9px] text-violet-900 truncate max-w-[72px]" title={orgHoliday.name}>
          {orgHoliday.name}
        </span>
      </div>
    );
  }

  if (mode === "future_empty") {
    return <span className="text-slate-400 text-sm">—</span>;
  }

  if (mode === "future_holiday") {
    return (
      <div className="flex flex-col items-center gap-1">
        <StatusBadge status="holiday" />
      </div>
    );
  }

  if (mode === "not_started") {
    return (
      <span className="text-[10px] leading-snug text-slate-400 max-w-[72px] inline-block">
        Day not started
      </span>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <StatusIcon status={rec?.status} />
      <PunchTimes checkIn={rec?.checkIn} checkOut={rec?.checkOut} compact />
    </div>
  );
}

function DetailRowCells({ rec, dateStr, orgHoliday }) {
  const { mode } = getAttendanceDisplayMode(dateStr, rec);

  if (orgHoliday && (mode === "future_empty" || !rec?.status || rec?.isEmpty)) {
    return (
      <>
        <td className="p-3 tabular-nums text-slate-400">—</td>
        <td className="p-3 tabular-nums text-slate-400">—</td>
        <td className="p-3 text-slate-400">—</td>
        <td className="p-3">
          <div className="flex flex-col gap-1">
            <StatusBadge status="holiday" />
            <span className="text-xs text-violet-800">{orgHoliday.name}</span>
          </div>
        </td>
      </>
    );
  }

  if (mode === "future_empty" || mode === "future_holiday") {
    return (
      <>
        <td className="p-3 tabular-nums text-slate-400">—</td>
        <td className="p-3 tabular-nums text-slate-400">—</td>
        <td className="p-3 text-slate-400">—</td>
        <td className="p-3">
          {mode === "future_holiday" ? (
            <StatusBadge status="holiday" />
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </td>
      </>
    );
  }

  if (mode === "not_started") {
    return (
      <>
        <td className="p-3 text-slate-400 text-sm">—</td>
        <td className="p-3 text-slate-400 text-sm">—</td>
        <td className="p-3 text-slate-400">—</td>
        <td className="p-3 text-slate-400 text-xs">Day not started</td>
      </>
    );
  }

  return (
    <>
      <td className="p-3 tabular-nums">{rec?.checkIn || "—"}</td>
      <td className="p-3 tabular-nums">{rec?.checkOut || "—"}</td>
      <td className="p-3">
        <HoursBar hours={rec?.hoursWorked || 0} />
      </td>
      <td className="p-3">
        <StatusBadge status={rec?.status} />
      </td>
    </>
  );
}

export default function AdminAttendanceView() {
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());

  const weeksInMonth = useMemo(() => getWeeksInMonth(year, month), [year, month]);

  const [weekIndex, setWeekIndex] = useState(() =>
    defaultWeekIndexForMonth(now.getFullYear(), now.getMonth(), getWeeksInMonth(now.getFullYear(), now.getMonth()))
  );

  const weekStart = weeksInMonth[weekIndex] ?? weeksInMonth[0] ?? null;

  const { employees, weekDays, weekLabel, loading, error } =
    useAdminAttendanceData(weekStart);

  const [holidays, setHolidays] = useState([]);
  const holidayByDate = useMemo(() => buildHolidayDateMap(holidays), [holidays]);

  const [selectedDayIndex, setSelectedDayIndex] = useState(0);

  const [departmentDraft, setDepartmentDraft] = useState("");
  const [statusDraft, setStatusDraft] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const yearOptions = useMemo(() => buildYearOptions(now.getFullYear()), []);

  useEffect(() => {
    if (weekDays.length) {
      setSelectedDayIndex(firstSelectableDayIndex(weekDays));
    }
  }, [weekStart, weekDays]);

  useEffect(() => {
    if (!weekDays.length) {
      setHolidays([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchWfHolidays({
          from_date: weekDays[0].date,
          to_date: weekDays[weekDays.length - 1].date,
        });
        if (!cancelled) setHolidays(Array.isArray(rows) ? rows : []);
      } catch {
        if (!cancelled) setHolidays([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [weekDays]);

  const handleMonthChange = (nextMonth) => {
    setMonth(Number(nextMonth));
    setWeekIndex(0);
  };

  const handleYearChange = (nextYear) => {
    setYear(Number(nextYear));
    setWeekIndex(0);
  };

  const prevDisabled = weekIndex <= 0;
  const nextDisabled = weekIndex >= weeksInMonth.length - 1;

  const selectedDate = weekDays[selectedDayIndex]?.date;
  const showFutureBanner = selectedDate && isFutureSelectedDay(selectedDate);

  const departments = useMemo(() => {
    const set = new Set(employees.map((e) => e.department).filter(Boolean));
    return ["", ...Array.from(set).sort()];
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    if (!departmentFilter) return employees;
    return employees.filter((e) => e.department === departmentFilter);
  }, [employees, departmentFilter]);

  const dayRows = useMemo(() => {
    if (!selectedDate) return [];
    return filteredEmployees
      .map((emp) => {
        const rec =
          emp.records?.find((r) => r.date === selectedDate) ||
          emp.records?.[selectedDayIndex];
        return { emp, rec };
      })
      .filter(({ rec }) => !statusFilter || rec?.status === statusFilter);
  }, [filteredEmployees, selectedDate, selectedDayIndex, statusFilter]);

  const dayStats = useMemo(() => {
    const all = filteredEmployees
      .map((emp) => {
        const rec =
          emp.records?.find((r) => r.date === selectedDate) ||
          emp.records?.[selectedDayIndex];
        if (!rec || !selectedDate) return null;
        const { mode } = getAttendanceDisplayMode(selectedDate, rec);
        if (mode === "future_empty" || mode === "not_started") return null;
        return rec;
      })
      .filter(Boolean);
    return {
      present: countByStatus(all, "present"),
      absent: countByStatus(all, "absent"),
      leave: countByStatus(all, "leave"),
      halfDay: countByStatus(all, "half-day"),
      holiday: countByStatus(all, "holiday"),
    };
  }, [filteredEmployees, selectedDate, selectedDayIndex]);

  const statCards = [
    { key: "present", label: "Present", value: dayStats.present, accent: "border-emerald-200" },
    { key: "absent", label: "Absent", value: dayStats.absent, accent: "border-red-200" },
    { key: "leave", label: "Leave", value: dayStats.leave, accent: "border-amber-200" },
    { key: "halfDay", label: "Half-Day", value: dayStats.halfDay, accent: "border-sky-200" },
    { key: "holiday", label: "Holiday", value: dayStats.holiday, accent: "border-violet-200" },
  ];

  const monthYearSelector = (
    <div className="flex items-center gap-2">
      <select
        value={month}
        onChange={(e) => handleMonthChange(e.target.value)}
        className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-800"
        aria-label="Select month"
      >
        {MONTH_NAMES.map((name, idx) => (
          <option key={name} value={idx}>
            {name}
          </option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => handleYearChange(e.target.value)}
        className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white text-slate-800"
        aria-label="Select year"
      >
        {yearOptions.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );

  const handleApplyFilters = () => {
    setDepartmentFilter(departmentDraft);
    setStatusFilter(statusDraft);
  };

  const handleClearFilters = () => {
    setDepartmentDraft("");
    setStatusDraft("");
    setDepartmentFilter("");
    setStatusFilter("");
  };

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6">
      <HrHero
        eyebrow={
          <span className="inline-flex items-center gap-2">
            <LayoutDashboard className="w-4 h-4" />
            Team Attendance
          </span>
        }
        title="Weekly attendance dashboard"
        subtitle={`${MONTH_NAMES[month]} ${year} — heatmap, holidays & day detail`}
        stats={statCards.map((c) => ({ label: c.label, value: c.value }))}
        actions={
          <WeekNavigator
            weekLabel={weekLabel}
            onPrev={() => setWeekIndex((i) => Math.max(0, i - 1))}
            onNext={() => setWeekIndex((i) => Math.min(weeksInMonth.length - 1, i + 1))}
            prevDisabled={prevDisabled}
            nextDisabled={nextDisabled}
            leading={monthYearSelector}
          />
        }
      />

      <div className="flex flex-wrap gap-2">
        {weekDays.map((day, idx) => {
          const disabled = isDayPillDisabled(day.date);
          return (
            <button
              key={day.date}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && setSelectedDayIndex(idx)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
                disabled
                  ? "bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed"
                  : holidayByDate[day.date]
                    ? selectedDayIndex === idx
                      ? "bg-violet-700 text-white border-violet-700"
                      : "bg-violet-100 border-violet-300 text-violet-900 hover:border-violet-400"
                    : day.isToday
                      ? selectedDayIndex === idx
                        ? "bg-slate-800 text-white border-slate-800"
                        : "bg-white border-teal-400 text-teal-800"
                      : weekendPillClass(day.label, selectedDayIndex === idx, false)
              }`}
            >
              {day.label}
              {day.isToday && !disabled && (
                <span className="ml-1 text-[10px] opacity-80">· today</span>
              )}
            </button>
          );
        })}
      </div>

      <HrPanel title="Filters" noPadding>
      <div className="hr-filter-bar border-0 shadow-none mb-0 rounded-none">
        <label className="text-sm text-slate-600 flex flex-col gap-1">
          Department
          <select
            value={departmentDraft}
            onChange={(e) => setDepartmentDraft(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white min-w-[160px]"
          >
            <option value="">All departments</option>
            {departments.filter(Boolean).map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-600 flex flex-col gap-1">
          Status
          <select
            value={statusDraft}
            onChange={(e) => setStatusDraft(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white min-w-[140px]"
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value || "all"} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={handleApplyFilters}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white"
          style={{ backgroundColor: COLORS.navy }}
        >
          Apply
        </button>
        <button
          type="button"
          onClick={handleClearFilters}
          className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-800 bg-white hover:bg-slate-50"
        >
          Clear
        </button>
      </div>
      </HrPanel>

      {error && (
        <div className="p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <BrixigoPageLoader compact message="Loading…" />
      ) : (
        <>
          <HrPanel title="Weekly heatmap" subtitle="Sat/Sun & gazetted holidays colour-coded" noPadding>
            <div className="hr-table-wrap">
            <table className="hr-table min-w-[800px]">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-600">
                  <th className="p-3 font-medium sticky left-0 bg-slate-50 z-10">Employee</th>
                  {weekDays.map((d) => (
                    <th
                      key={d.date}
                      className={`p-2 font-medium text-center w-24 ${heatmapHeaderClass(d.label)}`}
                      title={holidayByDate[d.date]?.name || undefined}
                    >
                      {d.label}
                      {holidayByDate[d.date] ? (
                        <span className="block text-[9px] font-normal text-violet-800 truncate max-w-[72px] mx-auto">
                          {holidayByDate[d.date].name}
                        </span>
                      ) : null}
                    </th>
                  ))}
                  <th className="p-3 font-medium text-right">Week hrs</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => {
                  const weekHrs = sumWeeklyHours(emp.records || []);
                  return (
                    <tr key={emp.employeeId} className="border-t border-slate-100">
                      <td className="p-3 sticky left-0 bg-white z-10">
                        <p className="font-medium text-slate-900">{emp.name}</p>
                        <p className="text-xs text-slate-500">{emp.department}</p>
                      </td>
                      {(emp.records || []).map((rec, dayIdx) => {
                        const dayMeta = weekDays[dayIdx];
                        const orgHoliday = holidayByDate[rec.date];
                        return (
                          <td
                            key={rec.date}
                            className={`p-2 text-center align-top ${heatmapTdClass(dayMeta?.label, orgHoliday)}`}
                          >
                            <HeatmapCell rec={rec} dateStr={rec.date} orgHoliday={orgHoliday} />
                          </td>
                        );
                      })}
                      <td className={`p-3 text-right tabular-nums ${weeklyHoursColor(weekHrs)}`}>
                        {weekHrs.toFixed(1)}h
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </HrPanel>

          <HrPanel
            title={`Day detail — ${weekDays[selectedDayIndex]?.label}`}
            subtitle={selectedDate}
            noPadding
          >

            {showFutureBanner && (
              <div className="mx-4 mt-4 p-3 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg">
                Data not available — future date
              </div>
            )}

            <div className="hr-table-wrap">
            <table className="hr-table min-w-[700px]">
              <thead>
                <tr className="bg-slate-50 text-left text-slate-600">
                  <th className="p-3">Employee</th>
                  <th className="p-3">Department</th>
                  <th className="p-3">Shift</th>
                  <th className="p-3">Check In</th>
                  <th className="p-3">Check Out</th>
                  <th className="p-3">Hours Worked</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {dayRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-6 text-center text-slate-500">
                      No records for this day and filters.
                    </td>
                  </tr>
                ) : (
                  dayRows.map(({ emp, rec }) => (
                    <tr key={emp.employeeId} className="border-t border-slate-100">
                      <td className="p-3 font-medium text-slate-900">{emp.name}</td>
                      <td className="p-3 text-slate-600">{emp.department}</td>
                      <td className="p-3 text-slate-600 text-xs">{emp.shift}</td>
                      <DetailRowCells
                        rec={rec}
                        dateStr={selectedDate}
                        orgHoliday={holidayByDate[selectedDate]}
                      />
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          </HrPanel>
        </>
      )}
    </div>
  );
}
